import { createSignal, For, Match, Show, Switch } from "solid-js";
import { Title } from "@solidjs/meta";
import { router } from "~/inertia";
import { Button } from "~/common/button";
import { Pill } from "~/common/pill";
import { PublicFormShell } from "~/common/public_form/shell";
import { PublicFormHeader } from "~/common/public_form/header";
import { SuccessCheck } from "~/common/public_form/success_check";
import { formatPhoneNumber } from "~/util/format";
import { familyVisitHubPath, visitHubPath } from "~/routes";
import { Icon } from "~/icon";
import {
  createPatientText,
  formatVisitTime,
  type PatientI18n,
  PatientTextProvider,
  splitAround,
  usePatientText,
} from "~/patient_i18n/i18n";
import { PatientLanguageBar } from "~/patient_i18n/language_bar";

type Header = {
  locationName: string;
  logoUrl: string | null;
  addressLines: string[] | null;
  phone: string | null;
};

type Visit = {
  id: string;
  patientFirstName: string;
  // ISO time on the office's clock; the page formats it in the patient's language.
  startsAt: string;
  timeZone: string;
  providerName: string | null;
  confirmed: boolean;
  hubToken: string;
  formsNeeded: number;
  addOnsAvailable: boolean;
};

type Props = {
  state: "confirmable" | "all_set" | "nothing_here";
  token: string;
  header: Header;
  // Echoed back by the confirm POST, same shape as the patient hub.
  anchorDate: string;
  appointments: Visit[];
  patientI18n: PatientI18n;
};

export default function FamilyVisitHubShow(props: Props) {
  // The language only changes on a full page load, so the strings are read once.
  // eslint-disable-next-line solid/reactivity
  const text = createPatientText(props.patientI18n);
  const t = text.t;

  return (
    <PatientTextProvider value={text}>
      <Title>{t("family_visit_hub.page_title", { location: props.header.locationName })}</Title>

      <PublicFormShell
        width="md"
        poweredByLabel={t("common.powered_by")}
        toolbar={<PatientLanguageBar />}
      >
        <PublicFormHeader
          location={{
            name: props.header.locationName,
            address: props.header.addressLines?.join(", ") ?? null,
            phone: props.header.phone,
            logoUrl: props.header.logoUrl,
          }}
          class="border-b border-brand-subtle bg-brand-light px-6 py-5"
        />

        <div class="flex flex-col gap-8 px-6 py-8">
          <Switch>
            <Match when={props.state === "confirmable"}>
              <ConfirmableBody
                appointments={props.appointments}
                token={props.token}
                anchorDate={props.anchorDate}
              />
            </Match>
            <Match when={props.state === "all_set"}>
              <AllSetBody appointments={props.appointments} />
            </Match>
            <Match when={props.state === "nothing_here"}>
              <NothingHereBody phone={props.header.phone} />
            </Match>
          </Switch>
        </div>
      </PublicFormShell>
    </PatientTextProvider>
  );
}

function ConfirmableBody(props: { appointments: Visit[]; token: string; anchorDate: string }) {
  const { t } = usePatientText();
  // Select-in: rows start unselected and clicking selects. After the confirm's
  // PRG the props reconcile in place (no remount); the fresh empty selection is
  // exactly right, since confirmed rows become pills and the rest await a new pick.
  const [picked, setPicked] = createSignal<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = createSignal(false);

  const unconfirmed = () => props.appointments.filter((visit) => !visit.confirmed);
  const selected = () => unconfirmed().filter((visit) => picked().has(visit.id));

  const toggle = (id: string) => {
    const next = new Set(picked());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setPicked(next);
  };

  const selectAll = () => {
    setPicked(new Set(unconfirmed().map((visit) => visit.id)));
  };

  const submit = () => {
    setConfirming(true);
    router.post(
      familyVisitHubPath(props.token),
      {
        anchorDate: props.anchorDate,
        appointmentIds: selected().map((visit) => visit.id),
        // proves which rows this render disclosed: the follow-up only counts
        // visits the family actually saw and left out.
        renderedIds: props.appointments.map((visit) => visit.id),
      },
      { onFinish: () => setConfirming(false) },
    );
  };

  const label = () => {
    if (selected().length === 0) return t("family_visit_hub.select_to_confirm");
    return t("family_visit_hub.confirm_visits", { count: selected().length });
  };

  const confirmedCount = () => props.appointments.filter((visit) => visit.confirmed).length;

  return (
    <>
      <Show
        when={confirmedCount() === 0}
        fallback={
          <div class="flex flex-col items-center gap-4 text-center">
            <SuccessCheck />
            <h1 class="text-2xl font-semibold text-typography-primary">
              {t("family_visit_hub.confirmed_of", {
                confirmed: confirmedCount(),
                total: props.appointments.length,
              })}
            </h1>
          </div>
        }
      >
        <div class="flex items-end justify-between gap-3">
          <div>
            <h1 class="text-2xl font-semibold text-typography-primary">
              {t("visit_hub.see_you_soon")}
            </h1>
            <p class="mt-2 text-sm text-typography-secondary">
              {t("family_visit_hub.select_prompt")}
            </p>
          </div>
          <Show when={selected().length < unconfirmed().length}>
            <Button type="button" variant="secondary" size="small" onClick={selectAll}>
              {t("family_visit_hub.select_all")}
            </Button>
          </Show>
        </div>
      </Show>

      <ul class="flex flex-col gap-3">
        <For each={props.appointments}>
          {(visit) => (
            <li>
              <Show when={!visit.confirmed} fallback={<VisitRow visit={visit} />}>
                <div
                  class="rounded-xl border transition"
                  classList={{
                    "border-brand-primary ring-1 ring-brand-primary/30": picked().has(visit.id),
                    "border-brand-subtle": !picked().has(visit.id),
                  }}
                >
                  <button
                    type="button"
                    class="w-full px-4 pt-3 text-left"
                    aria-pressed={picked().has(visit.id)}
                    onClick={() => toggle(visit.id)}
                  >
                    <span class="flex items-center justify-between gap-3">
                      <VisitDetails visit={visit} />
                      <Show
                        when={picked().has(visit.id)}
                        fallback={
                          <span class="size-6 shrink-0 rounded-full border-2 border-brand-subtle" />
                        }
                      >
                        <span class="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-primary text-white">
                          <Icon name="check" size="xs" />
                        </span>
                      </Show>
                    </span>
                  </button>
                  <div class="px-4 pb-3">
                    <VisitHubLink visit={visit} />
                  </div>
                </div>
              </Show>
            </li>
          )}
        </For>
      </ul>

      <div class="flex flex-col gap-2">
        <Button
          type="button"
          variant="primary"
          size="lg"
          width="full"
          loading={confirming()}
          disabled={confirming() || selected().length === 0}
          onClick={submit}
        >
          {label()}
        </Button>
      </div>
    </>
  );
}

function AllSetBody(props: { appointments: Visit[] }) {
  const { t } = usePatientText();

  return (
    <>
      <div class="flex flex-col items-center gap-4 text-center">
        <SuccessCheck />
        <div>
          <h1 class="text-2xl font-semibold text-typography-primary">
            {t("family_visit_hub.all_confirmed")}
          </h1>
          <p class="mt-1 text-sm text-typography-secondary">{t("visit_hub.see_you_soon")}</p>
        </div>
      </div>

      <ul class="flex flex-col gap-3">
        <For each={props.appointments}>
          {(visit) => (
            <li>
              <VisitRow visit={visit} />
            </li>
          )}
        </For>
      </ul>
    </>
  );
}

function NothingHereBody(props: { phone: string | null }) {
  const { t } = usePatientText();
  const callUs = () => splitAround(t("common.call_us"), "phone");

  return (
    <div class="flex flex-col items-center gap-3 py-6 text-center">
      <h1 class="text-xl font-semibold text-typography-primary">
        {t("family_visit_hub.nothing_here")}
      </h1>
      <Show when={props.phone}>
        {(phone) => (
          <p class="text-sm text-typography-secondary">
            {callUs()[0]}
            <a href={`tel:${phone()}`} class="font-medium text-brand-link hover:underline">
              {formatPhoneNumber(phone())}
            </a>
            {callUs()[1]}
          </p>
        )}
      </Show>
    </div>
  );
}

function VisitRow(props: { visit: Visit }) {
  const { t } = usePatientText();

  return (
    <div class="rounded-xl border border-brand-subtle px-4 py-3">
      <div class="flex items-center justify-between gap-3">
        <VisitDetails visit={props.visit} />
        <Show when={props.visit.confirmed}>
          <Pill variant="success" size="sm" class="shrink-0">
            {t("visit_hub.confirmed")}
          </Pill>
        </Show>
      </div>
      <VisitHubLink visit={props.visit} />
    </div>
  );
}

// The chip earns the tap: forms owed lead, then extras, then a quiet all
// clear. Every state links to the member's own hub, where the work lives.
function VisitHubLink(props: { visit: Visit }) {
  const { t } = usePatientText();
  const label = () => {
    if (props.visit.formsNeeded > 0) {
      return t("family_visit_hub.forms_needed", { count: props.visit.formsNeeded });
    }
    return t(
      props.visit.addOnsAvailable
        ? "family_visit_hub.extras_available"
        : "family_visit_hub.all_set",
    );
  };

  return (
    <a
      href={visitHubPath(props.visit.hubToken)}
      class="mt-1 inline-block rounded-full border px-2 py-0.5 text-xs hover:underline"
      classList={{
        "border-brand-primary bg-brand-light font-medium text-brand-primary":
          props.visit.formsNeeded > 0,
        "border-brand-subtle text-typography-secondary": props.visit.formsNeeded === 0,
      }}
    >
      {label()}
    </a>
  );
}

function VisitDetails(props: { visit: Visit }) {
  const { locale } = usePatientText();

  return (
    <span class="block min-w-0">
      <span class="block font-semibold text-typography-primary">
        {props.visit.patientFirstName}
      </span>
      <span class="block text-sm text-typography-secondary">
        {formatVisitTime(props.visit.startsAt, props.visit.timeZone, locale)}
      </span>
      <Show when={props.visit.providerName}>
        {(name) => (
          <span class="mt-0.5 block truncate text-sm text-typography-secondary">{name()}</span>
        )}
      </Show>
    </span>
  );
}
